import { create } from "zustand";
import type { AlgorithmOperation } from "@algoquest/schemas";
import type { SearchViewState } from "../algorithms/searching/SearchController";

/**
 * searchAlgorithmStore (Phase 04, replaces Phase 02/03's separate
 * algorithmStore / binaryAlgorithmStore): a single read-only Zustand
 * mirror of whichever SearchController is currently loaded, keyed by the
 * shared SearchViewState shape rather than an algorithm-specific one. The
 * engine/controller layer remains the sole source of truth — this store
 * never computes anything, it only reflects what GameRuntime reports via
 * callback. `choiceRejected` only ever gets set true by Binary Search
 * levels (Linear Search never rejects a "direction"), but keeping it here
 * rather than in a Binary-Search-only store is what lets the HUD read one
 * store regardless of which algorithm is loaded.
 */
interface SearchAlgorithmState {
  view: SearchViewState | null;
  trace: AlgorithmOperation[];
  rejectedHintIndex: number | null;
  choiceRejected: boolean;
  setView: (view: SearchViewState) => void;
  appendTrace: (operations: AlgorithmOperation[]) => void;
  setRejectedHint: (index: number | null) => void;
  setChoiceRejected: (rejected: boolean) => void;
  reset: (view: SearchViewState) => void;
}

export const useSearchAlgorithmStore = create<SearchAlgorithmState>((set) => ({
  view: null,
  trace: [],
  rejectedHintIndex: null,
  choiceRejected: false,
  setView: (view) => set({ view }),
  appendTrace: (operations) => set((state) => ({ trace: [...state.trace, ...operations] })),
  setRejectedHint: (rejectedHintIndex) => set({ rejectedHintIndex }),
  setChoiceRejected: (choiceRejected) => set({ choiceRejected }),
  reset: (view) => set({ view, trace: [], rejectedHintIndex: null, choiceRejected: false }),
}));
